import { useState } from "react";
import type { CannedScenario } from "../data/types";

interface Props {
  scenario: CannedScenario;
  onInvestigate: () => void;
}

export function InvestigateLanding({ scenario, onInvestigate }: Props) {
  const sev = scenario.triage_card.severity.toLowerCase();
  const f = scenario.incident.extracted_fields;

  const dispatched = scenario.dispatch_plan.filter((d) => d.invoke);
  const skipped = scenario.dispatch_plan.filter((d) => !d.invoke);

  const narrative = narrate(scenario);
  const rawSignals = collectRawSignals(scenario);
  const [showRaw, setShowRaw] = useState(false);

  return (
    <div className="landing">
      <div className="landing-hero">
        <div className="landing-hero-left">
          <div className="landing-tag">
            <span className={`badge severity-${sev} lg`}>{scenario.triage_card.severity}</span>
            <span className="landing-source-pill">{scenario.incident.source.toUpperCase()}</span>
            <span className="landing-id mono">{scenario.incident.incident_id}</span>
          </div>
          <h1 className="landing-title">{scenario.incident.title}</h1>
          <p className="landing-desc">{scenario.incident.short_description}</p>
        </div>
        <div className="landing-hero-right">
          <button
            type="button"
            className="button primary lg investigate-button"
            onClick={onInvestigate}
          >
            <span className="play-icon">▶</span> Investigate
          </button>
          <div className="investigate-subnote">
            Dispatches the triage coordinator
          </div>
        </div>
      </div>

      <div className="landing-grid">
        <section className="landing-card">
          <header className="landing-card-head">
            <span className="landing-card-eyebrow">What we know</span>
          </header>
          <div className="landing-card-body">
            <p className="narrative-lead">{narrative.lead}</p>
            {narrative.supporting && (
              <p className="narrative-supporting">{narrative.supporting}</p>
            )}
            <p className="narrative-meta">{narrative.when}</p>

            <button
              type="button"
              className="raw-signals-toggle"
              onClick={() => setShowRaw((s) => !s)}
              aria-expanded={showRaw}
            >
              <span className={`raw-signals-chevron ${showRaw ? "open" : ""}`}>›</span>
              {showRaw ? "Hide raw signals" : `View raw signals (${rawSignals.length})`}
            </button>

            {showRaw && (
              <DefList>
                {rawSignals.map((r) => (
                  <DefRow
                    key={r.k}
                    k={r.k}
                    v={r.v}
                    mono={r.mono}
                    fullLine={r.fullLine}
                  />
                ))}
              </DefList>
            )}
          </div>
        </section>

        <section className="landing-card">
          <header className="landing-card-head">
            <span className="landing-card-eyebrow">Coordinator routing plan</span>
            <span className="landing-card-meta muted">
              {dispatched.length} dispatch · {skipped.length} skip
            </span>
          </header>
          <div className="landing-card-body">
            <ul className="dispatch-preview">
              {scenario.dispatch_plan.map((d) => (
                <li
                  key={d.key}
                  className={`dispatch-row ${d.invoke ? "go" : "skip"}`}
                >
                  <span className="dispatch-mark" aria-hidden="true">
                    {d.invoke ? "→" : "×"}
                  </span>
                  <span className="dispatch-agent">
                    {agentLabel(d.key)}{" "}
                    <span
                      className={`dispatch-decision ${d.invoke ? "go" : "skip"}`}
                    >
                      {d.invoke ? "dispatch" : "skip"}
                    </span>
                  </span>
                  <span className="dispatch-reason muted">{d.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

function agentLabel(k: string): string {
  switch (k) {
    case "command":  return "Command-Line";
    case "identity": return "Identity";
    case "network":  return "Network";
    case "activity": return "Activity";
    default:         return k;
  }
}

// Compose a 2-3 sentence plain-English description of what the incoming
// incident actually says. Replaces a flat field dump that the analyst would
// have to mentally parse on their own.
function narrate(scenario: CannedScenario): {
  lead: string;
  supporting: string;
  when: string;
} {
  const f = scenario.incident.extracted_fields;
  const host = f.hostname ?? "the host";
  const dest = f.destination_domain ?? f.destination_ip ?? null;

  // Lead sentence — picks the most specific shape that applies
  let lead = "";
  if (f.command_line && f.process) {
    lead = `On ${host}, ${f.process} ran a command: ${truncate(f.command_line, 90)}.`;
  } else if (f.process && dest) {
    lead = `${f.process} on ${host} connected to ${dest}.`;
  } else if (f.user_email && dest) {
    lead = `${f.user_email} interacted with ${dest}.`;
  } else if (f.user_email && f.source_ip) {
    lead = `${f.user_email} signed in from ${f.source_ip}.`;
  } else if (f.user_email) {
    lead = `Event recorded for ${f.user_email}.`;
  } else if (f.hostname) {
    lead = `Event recorded on ${f.hostname}.`;
  } else {
    lead = "New incident received.";
  }

  // Supporting detail — anything not already in the lead
  const supports: string[] = [];
  if (f.bytes_transferred != null) {
    supports.push(`${fmtBytes(f.bytes_transferred)} transferred`);
  }
  if (f.parent_process && f.parent_process !== f.process) {
    supports.push(`process started by ${f.parent_process}`);
  }
  if (f.user_email && !lead.includes(f.user_email)) {
    supports.push(`acting user: ${f.user_email}`);
  }
  if (f.source_ip && !lead.includes(f.source_ip)) {
    supports.push(`source IP ${f.source_ip}`);
  }
  const supporting = supports.length
    ? supports.join(" · ").replace(/^./, (c) => c.toUpperCase()) + "."
    : "";

  // Meta: when + how detected + severity
  const sourceLabel = scenario.incident.source.replace(/_/g, " ").toUpperCase();
  const when = `Received ${scenario.incident.received_at_human ?? "just now"} from ${sourceLabel}. Alert severity: ${f.alert_severity}.`;

  return { lead, supporting, when };
}

interface RawSignal { k: string; v: string; mono?: boolean; fullLine?: boolean }

function collectRawSignals(scenario: CannedScenario): RawSignal[] {
  const f = scenario.incident.extracted_fields;
  const out: RawSignal[] = [];
  if (f.user_email)       out.push({ k: "User",            v: f.user_email,    mono: true });
  if (f.hostname)         out.push({ k: "Host",            v: f.hostname,      mono: true });
  if (f.source_ip)        out.push({ k: "Source",          v: f.source_ip,     mono: true });
  if (f.destination_ip || f.destination_domain) {
    out.push({ k: "Destination", v: f.destination_domain ?? f.destination_ip ?? "", mono: true });
  }
  if (f.process)          out.push({ k: "Process",         v: f.process,       mono: true });
  if (f.parent_process)   out.push({ k: "Parent process",  v: f.parent_process, mono: true });
  if (f.bytes_transferred != null) {
    out.push({ k: "Bytes transferred", v: fmtBytes(f.bytes_transferred), mono: true });
  }
  if (f.command_line) {
    out.push({ k: "Command line", v: truncate(f.command_line, 90), mono: true, fullLine: true });
  }
  out.push({ k: "Received",       v: scenario.incident.received_at, mono: true });
  out.push({ k: "Alert severity", v: f.alert_severity });
  return out;
}

function DefList({ children }: { children: React.ReactNode }) {
  return <dl className="def-list">{children}</dl>;
}

function DefRow({
  k,
  v,
  mono,
  fullLine,
}: {
  k: string;
  v: string;
  mono?: boolean;
  fullLine?: boolean;
}) {
  return (
    <div className={`def-row ${fullLine ? "full" : ""}`}>
      <dt className="def-key">{k}</dt>
      <dd className={`def-val ${mono ? "mono" : ""}`}>{v}</dd>
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n) + "…";
}
